import _ from "lodash";
import { memo, useCallback, useMemo, useRef } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { IClause, IDocument } from "../../interfaces";
import { ClauseDocumentList } from "./components/ClauseDocumentList";
import { RenderMetadata } from "./components/RenderMetadata";
import { transformTagsResponse } from "./helper";
import { useClauseDocumentInfoStyles } from "./styles";

export type ClauseDocumentInfoProps = {
  clause: IClause;
  onOpenDocument?: (document: IDocument) => void;
};

export const ClauseDocumentInfo = memo((props: ClauseDocumentInfoProps) => {
  const styles = useClauseDocumentInfoStyles();

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const scrollParentRef = useRef<HTMLDivElement>(null);

  const tags = transformTagsResponse(props.clause.mergedTagIds);

  const dataset = useMemo(() => {
    return Object.entries(
      _.transform(
        tags,
        (result, values, key) => {
          result[key] = _.sortBy(values, (value) => value.name.toLowerCase());
        },
        {} as Record<string, any>
      )
    );
  }, [tags]);

  const itemContent = useCallback(
    (index: number, data: [string, Array<{ id: string; name: string }>]) => (
      <RenderMetadata idx={index} data={data} context={{ className: styles.fieldItem }} />
    ),
    []
  );

  return (
    <div className={styles.container}>
      {props.clause.sourceDocuments?.[0] && (
        <ClauseDocumentList documents={props.clause.sourceDocuments} onOpenDocument={props.onOpenDocument} />
      )}
      <div ref={scrollParentRef} className={styles.virtuosoContainer}>
        <Virtuoso
          ref={virtuosoRef}
          data={dataset}
          className={styles.virtuoso}
          followOutput={(isBottom: boolean) => (isBottom ? "smooth" : false)}
          itemContent={itemContent}
          customScrollParent={scrollParentRef.current ?? undefined}
        />
      </div>
    </div>
  );
});
